import { type HistoryMessageModel } from '@mindverse/accessor-open';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import dynamic from 'next/dynamic';
import type { MouseEventHandler, ReactNode } from 'react';
import { forwardRef, memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import ContextBasicInfo from '@/components/chat-app/context/BasicInfo';
import useMindStorage, { EStorageKey } from '@/components/chat-app/hooks/useMindStorage';
// import useMindStorage, { EStorageKey } from '@/components/chat-app/hooks/useMindStorage';
import useMatch from '@/hooks/useMatch';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { HOMEPAGE_FRIEND_STATUS, HOMEPAGE_STATUS } from '@/service/homePage';
import type { ReactClassNameInterface } from '@/types/common';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import { ROUTER_PATH } from '@/utils/router';

import { INNER_EVENT } from '../../../../utils/utils';
import { MessageItemType } from '../../model/message-item';
import LoadMore from './LoadMore';
import styles from './index.less';

const Introduction = dynamic(() => import('@/ui/HomePage/Introduction'), {
  ssr: false
});

export const ScrollContainerIdentifier = 'chat-scroll-container';

interface IProps extends ReactClassNameInterface {
  children?: ReactNode | ReactNode[];
  mindId: string;
  msgListLength: number;
  userSendMsgListLength?: number;
  initialNoMore?: boolean;
  bigPadding: boolean;
  inMobile?: boolean;
  needLogin?: boolean;
  loadingMsgId?: string;
  fetchHistory: () => Promise<HistoryMessageModel>;
  onMouseMove?: MouseEventHandler<HTMLDivElement>;
}

const ScrollContainer = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const {
    className,
    children,
    mindId,
    msgListLength,
    initialNoMore,
    userSendMsgListLength,
    bigPadding,
    fetchHistory,
    onMouseMove: handleOnMouseMove
  } = props;
  const lowestLevelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const introductionRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const [layoutSize] = useStorage('layoutSize');

  const _inChat = !!useMatch(ROUTER_PATH.chat);
  const _inLog = !!useMatch(ROUTER_PATH.log);
  const inShare = _inChat || _inLog;
  const showHistory = !inShare;

  const { chatType } = useContext(ContextBasicInfo);
  const { homepageData } = useHomepageContext();
  // const [sessionId] = useMindStorage(EStorageKey.SESSION_ID);
  const [, setReady] = useMindStorage(EStorageKey.READY_TO_SET_CHAT_LIST);

  const foldLoadMore = useCallback((time: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.overflow = 'hidden';
    }

    setTimeout(() => {
      if (scrollContainerRef.current) {
        // scrollContainerRef.current.scrollTop = 66; //防止用户聊天历史记录抖动， 先注释
        scrollContainerRef.current.style.overflow = 'auto';
      }
    }, time);
  }, []);

  // 监听滚动到最底部
  useEffect(() => {
    function scrollToBottom(event: Event) {
      const e = event as CustomEvent;
      const { msgType } = e.detail ?? {};

      if (MessageItemType.umm === msgType) {
        return;
      }

      if (lowestLevelRef.current && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;

        setReady(true);
      }
    }

    const throttleScrollToBottom = throttle(scrollToBottom, 150, {
      leading: false,
      trailing: true
    });

    window.addEventListener(INNER_EVENT.SCROLL_TO_BOTTOM, throttleScrollToBottom);

    return () => {
      window.addEventListener(INNER_EVENT.SCROLL_TO_BOTTOM, throttleScrollToBottom);
    };
  }, [mindId]);

  useEffect(() => {
    setReady(false);
    setTimeout(() => {
      // 滚动到底部
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }

      setReady(true);
    }, 500);
  }, [layoutSize, msgListLength]);

  const HomepageIntroductionPortal = useMemo(() => {
    if (
      !!homepageData &&
      chatType === 'homepage' &&
      (homepageData.status === HOMEPAGE_STATUS.OPEN ||
        homepageData.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND)
    ) {
      return (
        <div ref={introductionRef}>
          <Introduction className="!mb-[20px]" homePageData={homepageData} />
        </div>
      );
    }

    return null;
  }, [chatType, homepageData]);

  return (
    <div ref={ref} className={classNames('h-full', className)}>
      <div
        ref={scrollContainerRef}
        className={classNames(styles.scrollContainer, 'h-fit max-h-full', {
          [styles.chatMode]: bigPadding
        })}
        id={ScrollContainerIdentifier}
        onMouseMove={handleOnMouseMove}
      >
        {/* <div className={styles.homepageLinearGradient} /> */}
        {showHistory ? (
          initialNoMore !== undefined ? (
            <>
              {initialNoMore ? (
                <div className="h-[20px]" />
              ) : (
                <LoadMore fetchHistory={fetchHistory} foldLoadMore={foldLoadMore} />
              )}
            </>
          ) : (
            <div className="h-[20px]" />
          )
        ) : (
          <div className="h-[20px]" />
        )}

        <div
          className={classNames(`chat-message-container`, styles.contentContainer)}
          id="chat-message-container"
        >
          {HomepageIntroductionPortal}

          {children}

          {!!userSendMsgListLength &&
          homepageData &&
          !homepageData.autoReply &&
          homepageData.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND ? (
            <div className="mx-auto my-5 flex flex-row justify-center items-center w-fit p-[6px] pr-[16px] gap-[6px] text-xs leading-5 text-primary-white bg-[rgba(0,0,0,0.1)] rounded-[40px] box-border border border-[rgba(255,255,255,0.1)]">
              {t('chatApp.notReplyTips', { name: homepageData.username || 'User' })}
            </div>
          ) : null}
        </div>
        <div ref={lowestLevelRef} className={styles.lowestLevel} />
      </div>
    </div>
  );
});

ScrollContainer.displayName = 'ScrollContainer';

export default memo(ScrollContainer);
